import { useEffect, ChangeEvent } from 'react';
import {
    Stack,
    TextField,
    Divider,
    Chip,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { updateFormData, setStepErrors } from '@/store/installSlice';

export const BasicConfigStep = () => {
    const { LL, setLocale } = useI18nContext();
    const dispatch = useAppDispatch();
    const formData = useAppSelector((state) => state.install.formData);

    // Validate this step
    useEffect(() => {
        const errors: string[] = [];
        if (!formData.domain) errors.push(LL.install.basicConfig.errors.domainRequired());
        if (!formData.dbDatabase) errors.push(LL.install.basicConfig.errors.dbNameRequired());
        if (!formData.dbUser) errors.push(LL.install.basicConfig.errors.dbUserRequired());

        dispatch(setStepErrors({ step: 0, errors }));
    }, [formData.domain, formData.dbDatabase, formData.dbUser, dispatch, LL]);

    const handleChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateFormData({ [field]: e.target.value }));
    };

    const handleLanguageChange = (newLanguage: string) => {
        dispatch(updateFormData({ defaultLanguage: newLanguage }));
        setLocale(newLanguage as 'en' | 'de');
    };

    return (
        <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
                {LL.install.basicConfig.description()}
            </Typography>

            <TextField
                label={LL.install.basicConfig.domain()}
                placeholder={LL.install.basicConfig.domainPlaceholder()}
                value={formData.domain}
                onChange={handleChange('domain')}
                required
                fullWidth
                helperText={LL.install.basicConfig.domainHelp()}
            />

            <FormControl fullWidth>
                <InputLabel>{LL.common.defaultLanguage()}</InputLabel>
                <Select
                    value={formData.defaultLanguage}
                    label={LL.common.defaultLanguage()}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                >
                    <MenuItem value="en">{LL.common.languageEnglish()}</MenuItem>
                    <MenuItem value="de">{LL.common.languageGerman()}</MenuItem>
                </Select>
            </FormControl>

            <Divider>
                <Chip label={LL.install.basicConfig.dbConnection()} />
            </Divider>

            <TextField
                label={LL.install.basicConfig.dbHost()}
                value={formData.dbHost}
                onChange={handleChange('dbHost')}
                required
                fullWidth
            />

            <TextField
                label={LL.install.basicConfig.dbName()}
                placeholder={LL.install.basicConfig.dbNamePlaceholder()}
                value={formData.dbDatabase}
                onChange={handleChange('dbDatabase')}
                required
                fullWidth
            />

            <TextField
                label={LL.install.basicConfig.dbUser()}
                placeholder={LL.install.basicConfig.dbUserPlaceholder()}
                value={formData.dbUser}
                onChange={handleChange('dbUser')}
                required
                fullWidth
            />

            <TextField
                label={LL.install.basicConfig.dbPassword()}
                type="password"
                value={formData.dbPassword}
                onChange={handleChange('dbPassword')}
                required
                fullWidth
            />
        </Stack>
    );
};
